import { useTranslation } from 'react-i18next';
import { Check, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Translation progress indicator for a survey card.
 *
 * Visual states (user requested: red → orange → green progress bar):
 *   • status="pending" & pct <  45%  → red "working" dot + red progress bar
 *   • status="pending" & pct >= 45%  → orange "almost there" dot + orange bar
 *   • status="success" (pct=100)     → green check, no bar
 *   • status="failed"                → yellow/amber warning + error message
 *
 * Renders NOTHING when meta is missing (e.g. old surveys created before v3.5.14).
 */
const TranslationStatusPill = ({ meta }) => {
    const { t } = useTranslation();
    if (!meta) return null;

    const status = meta.status || 'pending';
    const pct = Math.max(0, Math.min(100, Number(meta.progress_pct ?? 0)));
    const stage = meta.stage || '';

    if (status === 'success') {
        return (
            <div
                className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400"
                data-testid="translation-status-ready"
            >
                <Check className="w-3.5 h-3.5" />
                <span>{t('surveys.translationReady', 'Translations ready')}</span>
            </div>
        );
    }

    if (status === 'failed') {
        return (
            <div
                className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400"
                data-testid="translation-status-failed"
                title={meta.translation_error || ''}
            >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{t('surveys.translationFailed', 'Translation failed — retry from editor')}</span>
            </div>
        );
    }

    // Pending / in-progress — red → orange → (green handled by success above)
    const isAlmost = pct >= 45;
    const dotColor = isAlmost ? 'bg-orange-500' : 'bg-red-500';
    const barColor = isAlmost ? 'bg-orange-500' : 'bg-red-500';
    const label = stage.startsWith('translating_')
        ? t('surveys.translatingLang', 'Translating {{lang}}…', { lang: stage.split('_')[1].toUpperCase() })
        : stage === 'queued'
            ? t('surveys.translationQueued', 'Translation queued…')
            : t('surveys.translating', 'Translating…');

    return (
        <div
            className="space-y-1"
            data-testid={isAlmost ? 'translation-status-almost' : 'translation-status-working'}
        >
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="relative flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75`} />
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`} />
                </span>
                <span className="flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {label}
                </span>
                <span className="ml-auto text-[10px] font-mono text-muted-foreground">{pct}%</span>
            </div>
            <div className="h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                    className={`h-full ${barColor} transition-all duration-500 ease-out`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};

export default TranslationStatusPill;
